import { PairingListHeader } from "@components/pairingFastEntryList";
import { SymbolConstantDef, SymbolSpecificDef, SymbolVariableDef } from "@components/smart/GeneralFieldDefinition";
import { loanAccountFormatter } from "@components/smart/smartBankAccountFilter/SmartBankAccountFilter.utils";
import SmartFastEntryList, { ActionType, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IGroupRenderFn } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext from "@odata/BindingContext";
import { getNewItemsMaxId, setDirtyFlag } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    IPaymentDocumentEntity,
    IPaymentDocumentItemEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode, PaymentDocumentItemTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { PairingRow } from "@pages/banks/bankTransactions/BankTransactions.styles";
import {
    getTranBalance,
    PairCoreAdditionalData,
    PAYMENT_GROUP
} from "@pages/banks/bankTransactions/BankTransactions.utils";
import { getCoreDef, isPairFastEntryDisabled, PAIR_GROUP } from "@pages/banks/bankTransactions/BankTransactionsDef";
import { setNewItemDefaultCategoryData } from "@pages/banks/Payments.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import LoanTransactionsFormView from "@pages/loans/transactions/LoanTransactionsFormView";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { StatusCodeEnumTranslations } from "@pages/reports/CommonDefs";
import { isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { roundToDecimalPlaces } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import TestIds from "../../../testIds";
import { FormStorage } from "../../../views/formView/FormStorage";

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const def = getCoreDef(context, DocumentTypeCode.LoanTransaction);
    const isCashBasisAccounting = isCashBasisAccountingCompany(context);

    def.table.id = `${EntityTypeName.LoanTransaction}Table`;
    def.table.filterBarDef[0].filterDefinition["BankStatement/BankAccountBase"].formatter = loanAccountFormatter;
    def.table.filterBarDef[1].filterDefinition.DateBankTransaction.label = i18next.t("Banks:LoanTransactions.DateBankTransaction");
    def.table.columnDefinition.DateBankTransaction.label = i18next.t("Banks:LoanTransactions.DateBankTransaction");
    def.table.title = i18next.t("Banks:LoanTransactions.TableTitle");

    def.form.id = `${EntityTypeName.LoanTransaction}Form`;
    def.form.formControl = LoanTransactionsFormView;
    def.form.title = i18next.t("Banks:LoanTransactions.FormTitle");
    def.form.fieldDefinition.DateBankTransaction.label = i18next.t("Banks:LoanTransactions.DateBankTransaction");

    def.form.groups.find(group => group.id === PAIR_GROUP).title =
        isCashBasisAccounting ? i18next.t("Document:FormGroup.CashBasisAccounting") : i18next.t("Banks:Transactions.AccountAssignment");

    def.form.additionalProperties = [
        ...def.form.additionalProperties,
        ...PairCoreAdditionalData
    ];

    def.form.fieldDefinition = {
        ...def.form.fieldDefinition,
        SymbolVariable: { ...SymbolVariableDef },
        SymbolSpecific: { ...SymbolSpecificDef },
        SymbolConstant: { ...SymbolConstantDef }
    };

    const pairGroup = def.form.groups.find(group => group.id === PAIR_GROUP);
    pairGroup.lineItems.render = (args: IGroupRenderFn) => {
        const _change = (event: ISmartFieldChange) => {
            args.props.onChange(event);
            args.storage.refreshFields();
        };

        const _action = (e: ISmartFastEntriesActionEvent<IPaymentDocumentItemEntity>) => {
            const storage: FormStorage = args.storage as FormStorage;

            if (e.actionType === ActionType.Add) {
                const [newItem] = e.affectedItems;

                if (isCashBasisAccountingCompany(storage.context)) {
                    setNewItemDefaultCategoryData(newItem);
                }

                const balance = getTranBalance(storage) || 0;
                const entity = storage.data.entity as IPaymentDocumentEntity;
                const items = storage.getValue(e.bindingContext) as IPaymentDocumentItemEntity[];

                newItem.PaymentDocumentItemTypeCode = PaymentDocumentItemTypeCode.Other;
                newItem[BindingContext.NEW_ENTITY_ID_PROP] = getNewItemsMaxId(items) + 1;

                if (balance > 0) {
                    const tranExRate = entity.ExchangeRatePerUnit ?? 1;

                    newItem.TransactionAmount = roundToDecimalPlaces(2, balance);
                    newItem.Amount = roundToDecimalPlaces(2, balance * tranExRate);
                }

                storage.handleFirstChange();
            }

            setDirtyFlag(storage, e.bindingContext);
            args.props.onAction(e);

        };
        return (
            <div>
                <PairingRow data-testid={TestIds.PairingRow}>
                    <PairingListHeader
                        title={i18next.t("Banks:LoanTransactions.AccountingBalance")}
                        isDisabled={isPairFastEntryDisabled.bind(null, args.storage as FormStorage, false)}
                        isReadOnly={args.props.isReadOnly}
                        storage={args.storage as FormStorage}
                        bindingContext={args.props.bindingContext}
                    />
                </PairingRow>
                <SmartFastEntryList
                    isReadOnly={args.props.isReadOnly}
                    isDisabled={isPairFastEntryDisabled.bind(null, args.storage as FormStorage, false)}
                    useLabelWrapping={true}
                    groupId={PAYMENT_GROUP}
                    isCollapsible={false}
                    storage={args.storage as FormStorage}
                    bindingContext={args.props.bindingContext}
                    canAdd={true}
                    isTheOnlyItemRemovable
                    isItemCloneable={false}
                    renderAsFastEntryForReadOnly
                    columns={args.props.columns}
                    order={null}
                    onBlur={args.props.onBlur}
                    onChange={_change}
                    onAction={_action}
                />
            </div>
        );
    };

    if (def.form.fieldDefinition["Items/DateAccountingTransaction"]) {
        def.form.fieldDefinition["Items/DateAccountingTransaction"].isReadOnly = false;
    }

    return {
        ...def,
        entitySet: EntitySetName.LoanTransactions
    };
};

getDefinitions.translationFiles = [
    "Loans", "Chat", "Common", "Enums", "NumberRange", "Banks", "Document", "Error", "Categories", "FixedAsset",
    getEnumNameSpaceName(EntityTypeName.CbaCategoryTaxImpact), ...StatusCodeEnumTranslations];
setDefByEntityType(EntityTypeName.LoanTransaction, getDefinitions);